'use client';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CarouselProgress } from '../ui/carousel-progress';
import { BlogCard } from './blog-card';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';

export function RecommendedBlogs({ blogs }: { blogs: Blog[] }) {
  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-16 pr-0 pt-8 sm:pb-24 sm:pt-10 md:pb-32 lg:pb-36 lg:pt-16 xl:pt-[72px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex items-center justify-between border-b border-b-[#E5E7EB] pb-4 sm:pb-6 lg:pb-8',
        )}
      >
        <div className={cn('custom-container', 'flex items-end justify-between')}>
          <Heading subtitle="BLOG" title={t('text2469')} />
          <Button asChild variant={'outline'} className="hidden sm:flex">
            <Link prefetch={false} href={ROUTES.BLOG()}>
              {t('text2000')} <HiOutlineArrowRightCircle size={24} />
            </Link>
          </Button>
        </div>
      </div>
      <div className={cn('custom-container', 'relative')}>
        <div className="absolute -left-[1000px] top-0 z-[30] h-full w-[1000px] cursor-context-menu bg-white" />
        <Carousel
          options={options}
          className={{
            main: 'w-full overflow-visible',
            container: 'w-full overflow-visible',
            wrapper: 'w-full overflow-visible',
            slide: 'w-full max-w-[300px] sm:max-w-[334px] md:max-w-[390px] lg:max-w-[448px]',
          }}
          slides={blogs?.map(blog => <BlogCard blog={blog} key={blog.slug} />) ?? []}
        />
      </div>
      <div className="block w-full pr-4 sm:hidden">
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={blogs?.length ?? 0} />
      </div>
    </section>
  );
}
